import bingAdsTracker from '@/utils/Tracker/BingAds';
import Tracker from './tracker';
import TrackerQueue from './TrackerQueue';
import { armsErrorSend } from '../libs/armsErrorSend';
import { isEmpty } from 'lodash';

/**
 *
 * @description GA4 GTM 电子商务
 * @link https://developers.google.com/gtagjs/reference/ga4-events#add_to_cart
 * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 * @time 2022/11/2 17:22
 *
 */
class EcommerceTracker extends Tracker {
  /**
   *
   * @description 浏览商品 & 其他type类型处理
   * @param {Object} productDetail 当前产品详情
   * @param {Number} commodityId 当前选中套餐id
   *
   */
  viewItem = ({ productDetail = {}, commodityId, type, listName = '' }) => {
    const commodity = productDetail.commodities.filter((o) => o.id === commodityId)[0] ?? {};
    const discount =
      (!commodity.origin_price ? 0 : (commodity.origin_price?.amount ?? 0) - (commodity.price?.amount ?? 0)) ?? 0;

    try {
      this.commonEcommerceTracker({
        event: type ?? 'view_item',
        event_from: 'GA4',
        ecommerce: {
          currency: commodity?.price?.currency,
          value: commodity?.price?.amount,
          items: [
            {
              item_brand: 'Insta360',
              item_id: commodity?.id, // 商品id 这里传套餐id
              item_name: productDetail?.info.name, // 产品名
              item_category: commodity.info.name, // 套餐名
              item_list_name: listName,
              currency: commodity?.price?.currency,
              price: commodity?.price?.amount,
              discount,
              quantity: 1,
            },
          ],
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 浏览产品列表(首页、类目页)
   *
   */
  viewItemList = ({ item }) => {
    try {
      let items = [];
      if (Array.isArray(item)) {
        items = item.map((d) => ({ item_brand: 'Insta360', ...d }));
      } else {
        items = [
          {
            item_brand: 'Insta360',
            ...item,
          },
        ];
      }
      this.commonEcommerceTracker({
        event: 'view_item_list',
        event_from: 'GA4',
        ecommerce: {
          items,
        },
      });
    } catch (e) {
      armsErrorSend(e, { filename: 'GA4 EcommerceTracker viewItemList' });
    }
  };

  /**
   *
   * @description 点击产品列表item(首页、类目页)
   * @param {object} [productDetail] 如果传了productDetail / commodityId 就自动筛选item,currency,value
   * @param {number} [commodityId] cid
   * @param {string} [from] item_name_list
   *
   */
  selectItem = ({ item, currency, value, productDetail, commodityId, listName = '' }) => {
    try {
      if (productDetail && commodityId && !item) {
        return this.viewItem({ productDetail, commodityId, type: 'select_item', listName });
      }

      const items = [
        {
          item_brand: 'Insta360',
          item_list_name: listName,
          ...item,
        },
      ];

      this.commonEcommerceTracker({
        event: 'select_item',
        event_from: 'GA4',
        ecommerce: {
          currency,
          value,
          items,
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 曝光topbar、banner
   *
   */
  viewPromotion = ({ item }) => {
    try {
      const items = [
        {
          item_brand: 'Insta360',
          ...item,
        },
      ];
      this.commonEcommerceTracker({
        event: 'view_promotion',
        event_from: 'GA4',
        ecommerce: {
          promotion_id: item.item_id,
          items,
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 点击topbar、banner
   *
   */
  selectPromotion = ({ item }) => {
    try {
      const items = [
        {
          item_brand: 'Insta360',
          ...item,
        },
      ];
      this.commonEcommerceTracker({
        event: 'select_promotion',
        event_from: 'GA4',
        ecommerce: {
          promotion_id: item.item_id,
          items,
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 浏览商品
   * @param {Object} productDetail 当前产品详情
   * @param {Number} commodityId 当前选中套餐id
   *
   */
  viewProductToUA = ({ product }) => {
    // todo 停止上报 需要移除GTM相关配置
    try {
      const temp = {};
      const currencyCode = product.commodities[0].price.currency;
      temp.brand = 'Insta360';
      temp.id = product.id;
      temp.name = product.info.name;
      temp.price = product.commodities[0].price.amount;
      window.dataLayer.push({
        'event': 'viewProduct',
        'ecommerce': {
          'currencyCode': currencyCode,
          'detail': {
            'products': [temp],
          },
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 加入购物车
   * @param {Object} item 加入购物车的产品
   *
   */
  addToCart = ({ item = {}, listName = '' }) => {
    try {
      const { commodity = {}, name, count } = item;

      const discount =
        (!commodity.origin_price ? 0 : (commodity.origin_price?.amount ?? 0) - (commodity.price?.amount ?? 0)) ?? 0;
      this.commonEcommerceTracker({
        event: 'add_to_cart',
        event_from: 'GA4',
        ecommerce: {
          currency: commodity?.price?.currency,
          value: commodity?.price?.amount * (count ?? 1),
          items: [
            {
              item_brand: 'Insta360',
              item_id: commodity.id, // 商品id 这里传套餐id
              item_name: name, // 产品名
              item_category: commodity?.info?.name, // 套餐名
              currency: commodity?.price?.currency,
              price: commodity?.price?.amount,
              item_list_name: listName,
              discount,
              quantity: count,
            },
          ],
        },
      });

      // bingAdsTracker.addToCart({ value: commodity?.price?.amount, currency: commodity?.price?.currency });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 加入购物车
   * @param {Object} item 加入购物车的产品
   *
   */
  addToCartToUA = ({ items }) => {
    try {
      let currencyCode = '';
      const products = [];
      items.forEach((item) => {
        const temp = {};
        temp.brand = 'Insta360';
        temp.name = item.product.info.name;
        temp.id = item.product.id;
        temp.price = item.commodity.price.amount;
        currencyCode = item.commodity.price.currency;
        temp.quantity = item.count;
        products.push(temp);
      });
      window.dataLayer.push({
        'event': 'addToCart',
        'event_from': 'UA',
        'ecommerce': {
          'currencyCode': currencyCode,
          'add': {
            'products': products,
          },
        },
        'label': `product_${products[0].id}`,
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 套餐列表加入购物车
   * @param {Array} productCartItems 加入购物车的产品列表
   *
   */
  addToCartFromProductPage = ({ items = [] }) => {
    try {
      if (!items?.length) return;

      const ecommerce = {
        currency: '',
        value: 0,
        items: [],
      };

      items.forEach((commodity) => {
        const { id: cid, price = {}, origin_price, info: cInfo, count, product_name, commodity_name } = commodity;
        const discount = (!origin_price ? 0 : (origin_price?.amount ?? 0) - (price?.amount ?? 0)) ?? 0;
        ecommerce.currency = price?.currency;
        ecommerce.value += price?.amount * count * 1;

        const item = {
          item_brand: 'Insta360',
          item_id: cid, // 商品id 这里传套餐id
          item_name: product_name, // 产品名
          item_category: cInfo?.name ?? commodity_name, // 套餐名
          currency: price?.currency,
          price: price?.amount,
          discount,
          quantity: count,
        };

        ecommerce.items.push(item);
      });

      this.commonEcommerceTracker({
        event: 'add_to_cart',
        event_from: 'GA4',
        ecommerce,
      });

      // bingAdsTracker.addToCart({ value: ecommerce.value, currency: ecommerce.currency });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 查看购物车
   *
   */
  viewCart = ({ cartItems }) => {
    try {
      let totalAmount = 0;
      let currency = '';
      const items = cartItems.map((item) => {
        totalAmount += item.price.amount * item.count;
        currency = item.price.currency;
        let discount = 0;
        if (item.origin_price?.amount) {
          discount = item.origin_price.amount - item.price.amount;
        }
        return {
          item_brand: 'Insta360',
          item_id: item.commodityId, // 商品id 这里传套餐id
          item_name: item.product_name, // 产品名
          item_category: item.commodity.info.name, // 套餐名
          currency: item.price.currency,
          price: item.price.amount,
          discount,
          quantity: item.count,
        };
      });
      this.commonEcommerceTracker({
        event: 'view_cart',
        event_from: 'GA4',
        ecommerce: {
          currency,
          value: totalAmount.toFixed(2) * 1,
          items,
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 从购物车移除或减少数量
   *
   */
  removeFromCart = ({ cartItems }) => {
    try {
      let totalAmount = 0;
      let currency = '';
      const items = cartItems.map((item) => {
        totalAmount += item.price.amount * item.count;
        currency = item.price.currency;
        let discount = 0;
        if (item.origin_price?.amount) {
          discount = item.origin_price.amount - item.price.amount;
        }
        return {
          item_brand: 'Insta360',
          item_id: item.commodityId, // 商品id 这里传套餐id
          item_name: item.product_name, // 产品名
          item_category: item.commodity.info.name, // 套餐名
          currency: item.price.currency,
          price: item.price.amount,
          discount,
          quantity: item.count,
        };
      });
      this.commonEcommerceTracker({
        event: 'remove_from_cart',
        event_from: 'GA4',
        ecommerce: {
          currency,
          value: totalAmount.toFixed(2) * 1,
          items,
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 创建/更新收货地址
   *
   */
  addShippingInfo = ({ cartItems }) => {
    try {
      let totalAmount = 0;
      let currency = '';
      const items = cartItems.map((item) => {
        totalAmount += item.price.amount * item.count;
        currency = item.price.currency;
        let discount = 0;
        if (item.origin_price?.amount) {
          discount = item.origin_price.amount - item.price.amount;
        }
        return {
          item_brand: 'Insta360',
          item_id: item.commodityId, // 商品id 这里传套餐id
          item_name: item.product_name, // 产品名
          item_category: item.commodity.info.name, // 套餐名
          currency: item.price.currency,
          price: item.price.amount,
          discount,
          quantity: item.count,
        };
      });
      this.commonEcommerceTracker({
        event: 'add_shipping_info',
        event_from: 'GA4',
        ecommerce: {
          currency,
          value: totalAmount.toFixed(2) * 1,
          items,
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 衡量开始结帐
   * @param {Array} orderCartItems 创建订单的购物车产品列表
   *
   */
  beginCheckout = ({ cartItems = [] }) => {
    try {
      let totalAmount = 0;
      let currency = '';
      const items = cartItems.map((item) => {
        totalAmount += item.price.amount * item.count;
        currency = item.price.currency;
        let discount = 0;
        if (item.origin_price?.amount) {
          discount = item.origin_price.amount - item.price.amount;
        }
        return {
          item_brand: 'Insta360',
          item_id: item.commodityId, // 商品id 这里传套餐id
          item_name: item.product_name, // 产品名
          item_category: item.commodity.info.name, // 套餐名
          currency: item.price.currency,
          price: item.price.amount,
          discount,
          quantity: item.count,
        };
      });
      this.commonEcommerceTracker({
        event: 'begin_checkout',
        event_from: 'GA4',
        ecommerce: {
          currency,
          value: totalAmount.toFixed(2) * 1,
          items,
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 衡量结帐情况 - 此事件表示用户已开始结帐流程
   * @param {Array} orderCartItems 创建订单的购物车产品列表
   *
   */
  addPaymentInfo = ({ currency = '', items, coupon = '', value = 0, payment_type = '' }) => {
    try {
      this.commonEcommerceTracker({
        event: 'add_payment_info',
        event_from: 'GA4',
        ecommerce: {
          currency,
          value,
          payment_type,
          coupon,
          items: items.map((d) => ({
            item_id: d.commodity?.id,
            item_name: d.name,
            item_category: d.info?.name,
            price: d.price?.amount,
            quantity: d.count,
          })),
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  checkoutToUA = ({ items, step }) => {
    try {
      let currencyCode = '';
      const products = [];
      items.forEach((item) => {
        const temp = {};
        temp.brand = 'Insta360';
        temp.name = item.name;
        temp.id = item.productId;
        temp.price = item.price.amount;
        currencyCode = item.price.currency;
        temp.quantity = item.count;
        products.push(temp);
      });

      window.dataLayer.push({
        'event': 'checkout',
        'event_from': 'UA',
        'ecommerce': {
          'currencyCode': currencyCode,
          'checkout': {
            'actionField': { 'step': step },
            'products': products,
          },
        },
        'eventCallback': function () {},
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 衡量购物成功
   * @param {Object} orderInfo 结账成功订单信息
   *
   */
  purchase = ({ orderInfo }) => {
    const { orderNumber, orderPaymentInfo, orderItemInfos } = orderInfo;
    try {
      let items = orderItemInfos.map((item) => {
        return {
          item_brand: 'Insta360',
          item_id: item.commodityId, // 商品id 这里传套餐id
          item_name: item.productName, // 产品名
          item_category: item.commodityName,
          currency: item.currency,
          price: item.amount,
          discount: item.discountFee,
          quantity: item.number,
        };
      });
      this.commonEcommerceTracker({
        event: 'purchase',
        event_from: 'GA4',
        ecommerce: {
          transaction_id: orderNumber,
          affiliation: 'Insta360',
          value: orderPaymentInfo.totalAmount,
          tax: orderPaymentInfo.taxFee,
          shipping: orderPaymentInfo.shippingFee,
          currency: orderPaymentInfo.currency,
          items,
        },
      });
      bingAdsTracker.purchase({ value: orderPaymentInfo.totalAmount, currency: orderPaymentInfo.currency });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 电子商务事件 - 添加心愿单（统计下单商品）
   *
   */
  addToWishlist = ({ orderCartItems = [] }) => {
    try {
      const items = orderCartItems.map((item) => {
        return {
          item_brand: 'Insta360',
          item_id: item.commodityId, // 商品id 这里传套餐id
          item_name: item.product_name, // 产品名
          item_category: item?.info?.name,
          currency: item?.price?.currency,
          price: item?.price?.amount,
          quantity: item.count,
        };
      });
      this.commonEcommerceTracker({
        event: 'add_to_wishlist',
        event_from: 'GA4',
        ecommerce: {
          currency: orderCartItems[0]?.price?.currency,
          value: items.reduce((prev, cur) => prev + cur.price * cur.quantity, 0).toFixed(2) * 1,
          items,
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 衡量购物成功
   * @param {Object} orderInfo 结账成功订单信息
   *
   */
  purchaseToUA = ({ orderInfo }) => {
    try {
      const { orderItemInfos, orderPaymentInfo, contactEmail, orderNumber } = orderInfo;

      const products = orderItemInfos.map((item) => {
        const temp = {};
        temp.brand = 'Insta360';
        temp.name = item.productName;
        temp.id = item.productId;
        temp.price = item.amount;
        temp.quantity = item.number;
        return temp;
      });

      window.dataLayer.push({
        'event': 'purchase',
        'event_from': 'UA',
        'ecommerce': {
          'currencyCode': orderPaymentInfo.currency,
          'email': contactEmail,
          'purchase': {
            'actionField': {
              'id': orderNumber,
              'revenue': orderPaymentInfo.totalAmount,
              'tax': orderPaymentInfo.taxFee,
              'shipping': orderPaymentInfo.shippingFee,
              'coupon': orderPaymentInfo.giftCardFee + orderPaymentInfo.couponFee,
            },
            'products': products,
          },
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 衡量购物成功
   * @param {Object} orderInfo 结账成功订单信息
   *
   */
  purchaseToA8Tag = ({ orderInfo }) => {
    if (typeof window === 'undefined') return;

    try {
      const { orderItemInfos, orderPaymentInfo, orderNumber, area } = orderInfo;

      if (area !== 'JP') return;
      if (typeof window.a8sales !== 'function' || !window?.CookieConsentInstance) {
        return TrackerQueue.push('A8Tag', this.purchaseToA8Tag, [{ orderInfo }], this);
      }
      if (!window?.CookieConsentInstance?.canLoadSnippets('A8Tag')) return;

      const products = orderItemInfos.map((item) => {
        const temp = {};
        temp.brand = 'Insta360';
        temp.name = item.productName;
        temp.id = item.productId;
        temp.price = item.amount;
        temp.quantity = item.number;
        return temp;
      });

      const eData = {
        pid: 's00000018748001',
        order_number: orderNumber,
        currency: orderPaymentInfo.currency,
        items: products,
        total_price: orderPaymentInfo.totalAmount,
      };
      window.a8sales?.(eData);
    } catch (e) {
      armsErrorSend(e, { filename: 'BI- A8Tag' });
    }
  };

  /**
   *
   * @description 结账成功上报Yahoo
   *
   */
  purchaseToYahoo = ({ orderInfo }) => {
    if (typeof window === 'undefined') return;

    try {
      const { orderPaymentInfo, area } = orderInfo;
      if (area !== 'JP') return;
      if (!window?.CookieConsentInstance) {
        return TrackerQueue.push('Yahoo', this.purchaseToYahoo, [{ orderInfo }], this);
      }
      if (!window?.CookieConsentInstance?.canLoadSnippets('Yahoo')) return;

      const yahoo_conversion_id = 1001075541;
      const yahoo_conversion_label = 'ubcFCNXF9KEBEKzzvOEC';
      const yahoo_conversion_value = orderPaymentInfo?.totalAmount;

      window.yahoo_conversion_id = yahoo_conversion_id;
      window.yahoo_conversion_label = yahoo_conversion_label;
      window.yahoo_conversion_value = yahoo_conversion_value;

      // 加载sdk
      const s = document.createElement('script');
      s.src = 'https://s.yimg.jp/images/listing/tool/cv/conversion.js';
      s.async = true;
      document.body.appendChild(s);

      // 通过noscript img上报
      const img = document.createElement('img');
      img.height = 1;
      img.width = 1;
      img.style.display = 'none';
      img.src = `https://b91.yahoo.co.jp/pagead/conversion/${yahoo_conversion_id}?value=${yahoo_conversion_value}&label=${yahoo_conversion_label}&guid=ON&script=0&disvt=true`;
      img.alt = '';
      const noscript = document.createElement('noscript');
      noscript.appendChild(img);
      document.body.appendChild(noscript);
    } catch (e) {
      console.log(e);
    }
  };

  /**
   *
   * @description 结账成功上报Naver
   *
   */
  purchaseToNaver = ({ orderInfo }) => {
    if (typeof window === 'undefined') return;

    try {
      const { orderPaymentInfo, area } = orderInfo;
      if (area !== 'KR') return;
      if (!window.wcs_do || !window?.CookieConsentInstance) {
        return TrackerQueue.push('Naver', this.purchaseToNaver, [{ orderInfo }], this);
      }
      if (!window?.CookieConsentInstance?.canLoadSnippets('Naver')) return;

      window._nasa = window._nasa ?? {};
      window._nasa['cnv'] = window?.wcs?.cnv('1', orderPaymentInfo?.totalAmount);

      window.wcs_add = window.wcs_add ?? {};
      window.wcs_add['wa'] = 's_24867fffe3b8';

      window.wcs.inflow('insta360.com');

      // 调用 log 收集函数
      window.wcs_do(window._nasa);
    } catch (e) {
      console.error(e);
    }
  };

  addToCartToNaver = () => {
    if (typeof window === 'undefined') return;

    try {
      const { country } = window;
      if (country?.code !== 'KR') return;

      if (!window.wcs_do || !window?.CookieConsentInstance) {
        return TrackerQueue.push('Naver', this.addToCartToNaver, [], this);
      }
      if (!window?.CookieConsentInstance?.canLoadSnippets('Naver')) return;

      window._nasa = window._nasa ?? {};
      window._nasa['cnv'] = window?.wcs?.cnv('3', '1');

      window.wcs_add = window.wcs_add ?? {};
      window.wcs_add['wa'] = 's_24867fffe3b8';

      window.wcs.inflow('insta360.com');

      // 调用 log 收集函数
      window.wcs_do(window._nasa);
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * @description 结账成功上报百度统计
   */
  purchaseToBaiduHm = ({ orderInfo }) => {
    if (typeof window === 'undefined') return;

    if (!window._hmt || !window?.CookieConsentInstance) {
      return TrackerQueue.push('BaiduHm', this.purchaseToBaiduHm, [{ orderInfo }], this);
    }
    if (!window?.CookieConsentInstance?.canLoadSnippets('BaiduAds')) return;

    try {
      const { orderItemInfos, orderPaymentInfo } = orderInfo;
      window._hmt?.push([
        '_trackOrder',
        {
          'orderId': orderInfo.orderNumber,
          'orderTotal': orderPaymentInfo.totalAmount,
          'item': orderItemInfos.map((item) => ({
            'skuId': item.commodityId,
            'skuName': `${item.productName} ${item.commodityName}`,
            'category': item.productName,
            'Price': item.amount,
            'Quantity': item.number,
          })),
        },
      ]);
    } catch (e) {
      armsErrorSend(e, { filename: 'BI- BaiduHM' });
    }
  };

  setEnhancedConversionData = ({ orderInfo }) => {
    window.dataLayer.push({
      'enhanced_conversion_data': {
        'email': orderInfo?.contactEmail,
      },
    });
  };

  /**
   * TTD埋点事件
   * @param {pageView | addToCart | purchase} event
   * @param {*} data
   */
  trackTTD = (event, data) => {
    if (typeof window === 'undefined') return;

    if (!window.__ttd || !window?.CookieConsentInstance) {
      return TrackerQueue.push('TTD', this.trackTTD, [event, data], this);
    }
    // 广告主ID
    const ADVERTISER_ID = 'n7q8h4r';
    // 事件ID，加购事件是直接添加img标签
    const UPIXEL_ID = {
      pageView: 'k4kijfl',
      purchase: '56mmylr',
    };
    switch (event) {
      case 'pageView':
      case 'purchase':
        window.__ttd?.init?.(
          ADVERTISER_ID,
          [UPIXEL_ID[event]],
          'https://insight.adsrvr.org/track/up',
          isEmpty(data)
            ? undefined
            : {
                orderid: data.orderNumber,
                v: data.amount,
                vf: data.currency,
              }
        );
        break;
      case 'addToCart':
        const img = document.createElement('img');
        img.height = 1;
        img.width = 1;
        img.style.display = 'none';
        img.alt = '';
        img.src = 'https://insight.adsrvr.org/track/pxl/?adv=n7q8h4r&ct=0:izv7v3b&fmt=3';

        const noscript = document.createElement('noscript');
        noscript.appendChild(img);
        document.body.appendChild(noscript);
        break;
      default:
        break;
    }
  };
}

const T = new EcommerceTracker();
export default T;
